import type { TranslateFn } from '@/i18n/useTimePilotT';

/**
 * Decision for how the assistant chat should react when a submit fails *before*
 * the request reaches the backend (e.g. a missing/expired cell session throws in
 * onQuerySubmit). Previously this path silently reset the input to empty with no
 * answer and no error — the user saw nothing happen. We now always:
 *   - preserve the typed question (clearQuery: false) so it can be retried, and
 *   - surface a user-facing message.
 */

export interface SubmitFailureOutcome {
  /** Whether to clear the question box. Always false: never lose the user's text on error. */
  clearQuery: boolean;
  /** Inline message to show beneath the question bar. */
  errorMessage: string;
}

/** A missing/expired cell session is the common, actionable case — name it. */
const SESSION_ERROR_MESSAGE =
  'Your question could not be sent. Your session may have expired — please try again.';
const GENERIC_ERROR_MESSAGE =
  'Your question could not be sent. Please try again.';

export function describeSubmitFailure(error: unknown, t?: TranslateFn): SubmitFailureOutcome {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const isSessionError = /cell session|missing cell|session/i.test(message);
  return {
    clearQuery: false,
    errorMessage: t
      ? t(isSessionError ? 'chat.errors.sessionSubmit' : 'chat.errors.genericSubmit')
      : isSessionError
        ? SESSION_ERROR_MESSAGE
        : GENERIC_ERROR_MESSAGE,
  };
}
